// Albatross flight analysis API service
// Routes through our backend proxy (/albatross/*) to avoid browser CORS restrictions.
// The proxy Lambda forwards requests server-side to the albatross API.
const _apiBase = (import.meta.env.VITE_API_URL as string | undefined) ||
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
  'http://localhost:3001'

const BASE = `${_apiBase}/albatross`

function getAuthHeaders(): Record<string, string> {
  try {
    const stored = localStorage.getItem('fwo_auth_session')
    if (!stored) return {}
    const session = JSON.parse(stored) as Record<string, unknown>
    const token = typeof session.idToken === 'string' ? session.idToken : null
    return token ? { Authorization: `Bearer ${token}` } : {}
  } catch {
    return {}
  }
}

export interface AlbatrossTask {
  id: string
  name: string
  date: string
  status?: string
  participants?: AlbatrossParticipant[]
  // Raw XCTrack task definition (JSON-encoded). Contains turnpoints with waypoints
  // (lat/lon/name) and radius — used to overlay task geometry on the thermal map.
  xctsk?: string
}

export interface XctskTurnpoint {
  radius: number
  waypoint: {
    lat: number
    lon: number
    name?: string
    description?: string
    altSmoothed?: number
  }
  type?: 'TAKEOFF' | 'SSS' | 'ESS'
}

export interface XctskTask {
  version: number
  taskType?: string
  turnpoints: XctskTurnpoint[]
  sss?: { type?: string; direction?: string; timeGates?: string[] }
  goal?: { type?: string; deadline?: string }
}

export function parseXctsk(raw: string | undefined): XctskTask | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as XctskTask
    return Array.isArray(parsed.turnpoints) ? parsed : null
  } catch {
    return null
  }
}

export interface AlbatrossParticipant {
  id: string
  name: string
  civlId?: string
  participantNumber?: string
}

export interface PilotComparisonStats {
  id: string
  name: string
  climbRate?: number | null
  glideRatio?: number | null
  speed?: number | null
  decisionQuality?: number | null
  rank?: number | null
  goalReached?: boolean | null
}

export interface TaskComparison {
  taskId: string
  taskName?: string
  date?: string
  pilot1: PilotComparisonStats
  pilot2: PilotComparisonStats
}

export async function fetchTasks(compId: string): Promise<AlbatrossTask[]> {
  // Note: tasks list is unversioned; per-task subresources (flights, thermal-atlas,
  // comparison) live under /v1. The competitions and coaching endpoints are similarly split.
  const url = `${BASE}/competitions/${encodeURIComponent(compId)}/tasks`
  const res = await fetch(url, { headers: getAuthHeaders() })
  if (!res.ok) throw new Error(`Albatross API error ${res.status} fetching ${url}`)
  const data = await res.json()
  // API returns { success, data: [...] }
  return Array.isArray(data.data) ? data.data : []
}

export async function fetchCompetitionParticipants(_compId: string, tasks: AlbatrossTask[]): Promise<AlbatrossParticipant[]> {
  // No dedicated participants endpoint — extract unique pilots from task flights
  if (tasks.length === 0) return []
  const firstTask = tasks[0]
  const flightsUrl = `${BASE}/v1/tasks/${encodeURIComponent(firstTask.id)}/flights`
  const res = await fetch(flightsUrl, { headers: getAuthHeaders() })
  if (!res.ok) throw new Error(`Albatross API error ${res.status} fetching ${flightsUrl}`)
  const data = await res.json()
  // API returns { data: { items: [...] } } for paginated flights
  const items: unknown[] = data?.data?.items ?? []
  const seen = new Map<string, AlbatrossParticipant>()
  for (const item of items) {
    if (!item || typeof item !== 'object') continue
    const r = item as Record<string, unknown>
    const name = String(r.pilot ?? '')
    const compNo = String(r.compId ?? '')
    if (!name) continue
    const key = name.toLowerCase()
    if (!seen.has(key)) {
      seen.set(key, {
        id: name,
        name,
        participantNumber: compNo || undefined,
      })
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name))
}

export interface CoachingRecommendation {
  priority: 'high' | 'medium' | 'low' | string
  category: string
  title: string
  detail: string
}

export interface CoachingNote {
  id: string
  taskId: string
  taskName: string
  date: string
  narrative: string
  rank?: number
  totalPilots?: number
  madeGoal?: boolean
  taskCompletionPct?: number
  strengths: string[]
  improvements: string[]
  recommendations: CoachingRecommendation[]
}

export async function fetchCoachingNotes(
  compId: string,
  pilotIdentifier: string
): Promise<CoachingNote[]> {
  // Step 1: get tasks for the competition
  const tasks = await fetchTasks(compId)
  if (tasks.length === 0) return []

  // For each task, run flights → coaching as a serial chain, but run all
  // task chains in parallel. Removes the artificial Step-2/Step-3 phase
  // boundary so a fast task's coaching can fire while a slow task is still
  // fetching flights.
  const notes = await Promise.all(
    tasks.map(async (task): Promise<CoachingNote | null> => {
      let flightId: string
      try {
        const flightUrl = `${BASE}/v1/tasks/${encodeURIComponent(task.id)}/flights`
        const res = await fetch(flightUrl, { headers: getAuthHeaders() })
        if (!res.ok) {
          console.error(`Albatross API: GET ${flightUrl} returned ${res.status}`)
          return null
        }
        const data = await res.json()
        const items: unknown[] = data?.data?.items ?? []
        const flight = items.find((item): item is Record<string, unknown> => {
          if (!item || typeof item !== 'object') return false
          const r = item as Record<string, unknown>
          const name = String(r.pilot ?? '').toLowerCase()
          const compNo = String(r.compId ?? '')
          const id = pilotIdentifier.toLowerCase()
          return name === id || compNo === pilotIdentifier
        })
        if (!flight) return null
        flightId = String(flight.id ?? flight.flightId ?? '')
        if (!flightId) return null
      } catch (err) {
        console.error(`Albatross API: failed fetching flights for task ${task.id}`, err)
        return null
      }

      try {
        // /v1/coaching requires BOTH flightId and taskId — flightId alone returns 400.
        const url = `${BASE}/v1/coaching?flightId=${encodeURIComponent(flightId)}&taskId=${encodeURIComponent(task.id)}`
        const res = await fetch(url, { headers: getAuthHeaders() })
        if (!res.ok) {
          console.error(`Albatross API: GET ${url} returned ${res.status}`)
          return null
        }
        const json = await res.json()
        const d = (json?.data ?? json) as Record<string, unknown>
        const summary = (d?.summary ?? {}) as Record<string, unknown>
        const narrative = String(summary.narrativeSummary ?? '')
        if (!narrative) return null
        const rawRecs = Array.isArray(d?.recommendations) ? d.recommendations : []
        const recommendations = rawRecs
          .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
          .map((r) => ({
            priority: String(r.priority ?? 'low'),
            category: String(r.category ?? ''),
            title: String(r.title ?? ''),
            detail: String(r.detail ?? ''),
          }))
        return {
          id: flightId,
          taskId: task.id,
          taskName: task.name,
          date: task.date,
          narrative,
          rank: typeof summary.overallRank === 'number' ? summary.overallRank : undefined,
          totalPilots: typeof summary.totalPilots === 'number' ? summary.totalPilots : undefined,
          madeGoal: typeof summary.madeGoal === 'boolean' ? summary.madeGoal : undefined,
          taskCompletionPct: typeof summary.taskCompletionPct === 'number' ? summary.taskCompletionPct : undefined,
          strengths: Array.isArray(summary.strengthAreas) ? summary.strengthAreas.map(String) : [],
          improvements: Array.isArray(summary.improvementAreas) ? summary.improvementAreas.map(String) : [],
          recommendations,
        } satisfies CoachingNote
      } catch (err) {
        console.error(`Albatross API: failed fetching coaching for flightId ${flightId}`, err)
        return null
      }
    })
  )

  return notes.filter((n): n is CoachingNote => n !== null)
}

export async function fetchTaskComparison(
  taskId: string,
  pilot1Id: string,
  pilot2Id: string
): Promise<TaskComparison> {
  const url = `${BASE}/v1/tasks/${encodeURIComponent(taskId)}/comparison?pilots=${encodeURIComponent(pilot1Id)},${encodeURIComponent(pilot2Id)}`
  const res = await fetch(url, { headers: getAuthHeaders() })
  if (!res.ok) throw new Error(`Albatross API error ${res.status}`)
  const json = await res.json()
  // API returns { data: { cards: [...], superlatives, ... } } — transform to pilot1/pilot2 shape
  const data = json?.data ?? json
  const cards: Record<string, unknown>[] = Array.isArray(data?.cards) ? data.cards : []
  if (cards.length < 2) throw new Error('Insufficient comparison data')
  const mapCard = (c: Record<string, unknown>): PilotComparisonStats => {
    const m = (c.metrics ?? {}) as Record<string, unknown>
    return {
      id: String(c.flightId ?? c.pilotName ?? ''),
      name: String(c.pilotName ?? ''),
      climbRate: m.avgClimbRate != null ? Number(m.avgClimbRate) : null,
      glideRatio: m.avgGlideRatio != null ? Number(m.avgGlideRatio) : null,
      speed: m.xcSpeed != null ? Number(m.xcSpeed) : null,
      decisionQuality: null,
      rank: null,
      goalReached: c.madeGoal != null ? Boolean(c.madeGoal) : null,
    }
  }
  // Match cards to requested pilots by name
  const c1 = cards.find(c => String(c.pilotName).toLowerCase() === pilot1Id.toLowerCase()) ?? cards[0]
  const c2 = cards.find(c => String(c.pilotName).toLowerCase() === pilot2Id.toLowerCase()) ?? cards[1]
  return { taskId, pilot1: mapCard(c1), pilot2: mapCard(c2) }
}

export interface AlbatrossCompetition {
  id: string
  name: string
  location: string
  startDate: string
  endDate: string
}

export async function fetchAlbatrossCompetitions(): Promise<AlbatrossCompetition[]> {
  const url = `${BASE}/competitions`
  const res = await fetch(url, { headers: getAuthHeaders() })
  if (!res.ok) {
    console.error(`Albatross API: GET ${url} returned ${res.status}`)
    return []
  }
  const json = await res.json()
  return json.data || []
}
